import { useState, useRef, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../store/authStore";
import { notificationService } from "../../services/notifications";
import {
  LayoutDashboard, Users, UserCircle, FolderKanban, CheckSquare,
  FileText, Users2, BarChart3, Settings, LogOut, Bell, CheckCheck, HeartHandshake,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const Layout = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: notificationService.getAll,
    refetchInterval: 30000,
  });

  const markRead = useMutation({
    mutationFn: (id) => notificationService.markAsRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllRead = useMutation({
    mutationFn: notificationService.markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "admin";
  const isPartner = user?.role === "partner";

  const allNavItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/leads", icon: Users, label: "Leads", hide: isPartner },
    { to: "/clients", icon: UserCircle, label: "Clients", hide: isPartner },
    { to: "/projects", icon: FolderKanban, label: "Projects", hide: isPartner },
    { to: "/tasks", icon: CheckSquare, label: "Tasks", hide: isPartner },
    { to: "/proposals", icon: FileText, label: "Proposals", hide: isPartner },
    { to: "/team", icon: Users2, label: "Team", hide: isPartner },
    { to: "/partners", icon: HeartHandshake, label: "Partners", hide: !isAdmin && !isPartner },
    { to: "/reports", icon: BarChart3, label: "Reports", hide: isPartner },
    { to: "/settings", icon: Settings, label: "Settings", hide: isPartner },
  ];

  const navItems = allNavItems.filter((item) => !item.hide);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const currentPage = navItems.find((item) => isActive(item.to))?.label || "Dashboard";
  const unreadCount = notifData?.unreadCount || 0;
  const notifications = notifData?.notifications || [];

  return (
    <div className="flex h-screen bg-gray-50">
      <aside className="w-64 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col shadow-xl">
        <div className="px-6 py-5 border-b border-white/10">
          <h1 className="text-xl font-bold tracking-tight">
            <span className="text-blue-400">Deeplink</span> CRM
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">Creators Management</p>
        </div>
        <nav className="flex-1 mt-2 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive(item.to)
                  ? "bg-blue-500/20 text-blue-400 shadow-sm"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <item.icon className={`w-5 h-5 mr-3 ${isActive(item.to) ? "text-blue-400" : ""}`} />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 mx-3 mb-3">
          <div className="flex items-center mb-3">
            <Link to="/profile" className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-sm font-bold shadow-lg hover:ring-2 hover:ring-blue-400 transition-all">
              {user?.name?.[0]?.toUpperCase()}
            </Link>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-3 py-2 text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{currentPage}</h2>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifs(!showNotifs)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllRead.mutate()}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-center text-sm text-gray-400 py-8">No notifications</p>
                    ) : (
                      notifications.slice(0, 15).map((n) => (
                        <div
                          key={n._id}
                          onClick={() => !n.read && markRead.mutate(n._id)}
                          className={`px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${
                            !n.read ? "bg-blue-50/50" : ""
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.read && <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />}
                            <div className={!n.read ? "" : "ml-4"}>
                              <p className="text-sm font-medium text-gray-900">{n.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{n.message}</p>
                              <p className="text-[10px] text-gray-400 mt-1">
                                {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
